import React, { PropsWithChildren } from 'react'
import { StyleSheet, View, Text, ViewStyle } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated'

const defaultMinWidth = 300

interface DraggableProps {
  minWidth?: number
  showHandle?: boolean
  containerStyle?: Exclude<ViewStyle, 'transform'>
}
export const Draggable = ({
  children,
  minWidth = defaultMinWidth,
  showHandle = true,
  containerStyle
}: PropsWithChildren<DraggableProps>) => {
  // Shared values for tracking position
  const translateX = useSharedValue(0)
  const translateY = useSharedValue(0)

  // Store the context for gesture calculations
  const contextX = useSharedValue(0)
  const contextY = useSharedValue(0)

  // Create a pan gesture using the new Gesture API
  const panGesture = Gesture.Pan()
    .onStart(() => {
      contextX.value = translateX.value
      contextY.value = translateY.value
    })
    .onUpdate((event) => {
      translateX.value = contextX.value + event.translationX
      translateY.value = contextY.value + event.translationY
    })
    .onEnd(() => {
      // Optional: Add bounce effect when released
      translateX.value = withSpring(translateX.value)
      translateY.value = withSpring(translateY.value)
    })

  // Animated styles for the component
  const rStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }, { translateY: translateY.value }]
    }
  })

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View style={[styles.container, { width: minWidth }, containerStyle, rStyle]}>
        {showHandle && (
          <View style={[styles.header, { position: 'absolute', width: '100%', zIndex: 0 }]}>
            <View style={styles.dragHandle} />
          </View>
        )}
        {children}
      </Animated.View>
    </GestureDetector>
  )
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5
  },
  header: {
    padding: 10,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    alignItems: 'center'
  },
  dragHandle: {
    width: 40,
    height: 5,
    backgroundColor: '#c0c0c0',
    borderRadius: 3,
    marginBottom: 10
  }
})
